import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Users2, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Pinned squad summary + today's check-in status with a one-click nudge to everyone outstanding. */
export default function SquadStrip({ athletes, squads, onReload }) {
  const [squad, setSquad] = useState("");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/squad/summary${squad ? `?squad=${encodeURIComponent(squad)}` : ""}`)
      .then((r) => setS(r.data)).catch(() => setS(null));
  }, [squad, athletes]);

  const today = new Date().toISOString().slice(0, 10);
  const pool = squad ? athletes.filter((a) => a.squad === squad) : athletes;
  const outstanding = pool.filter((a) => a.last_data_at !== today);

  const nudge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/squad/nudge", { athlete_ids: outstanding.map((a) => a.athlete_id) });
      toast.success(`Nudged ${data.nudged} athlete(s)`, { description: data.note });
      onReload?.();
    } catch { toast.error("Could not send the nudges"); }
    finally { setBusy(false); }
  };

  if (!athletes.length) return null;

  return (
    <div className="border-b border-border" data-testid="squad-strip">
      <div className="px-4 sm:px-8 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground">
          <Users2 className="w-4 h-4" /> Squad
        </span>
        <select data-testid="squad-strip-select" value={squad} onChange={(e) => setSquad(e.target.value)}
          className="inp w-auto text-base py-2.5 min-w-[220px] font-medium">
          <option value="">Population · all squads</option>
          {squads.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        {[["Athletes", s?.athletes], ["Avg readiness", s?.avg_readiness], ["High injury risk", s?.high_injury_risk],
          ["Unavailable", s?.unavailable], ["No check-in today", s?.missing_checkin]].map(([k, v]) => (
          <span key={k} className="flex items-baseline gap-1.5" data-testid={`strip-${String(k).toLowerCase().replace(/ /g, "-")}`}>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</span>
            <span className="font-heading font-black tabular-nums">{v ?? "-"}</span>
          </span>
        ))}
        {outstanding.length > 0 && (
          <button data-testid="nudge-btn" onClick={nudge} disabled={busy}
            className="ml-auto text-xs border border-border px-2.5 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
            Nudge {outstanding.length} outstanding
          </button>
        )}
      </div>
      {outstanding.length > 0 && (
        <div className="px-4 sm:px-8 pb-3 text-[11px] text-muted-foreground" data-testid="checkin-outstanding">
          Awaiting today's check-in:{" "}
          {outstanding.slice(0, 8).map((a) => `${a.name}${a.last_data_at ? ` (last ${fmtDate(a.last_data_at)})` : ""}`).join(" · ")}
          {outstanding.length > 8 ? ` +${outstanding.length - 8} more` : ""}
        </div>
      )}
    </div>
  );
}
